import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  LocationOn,
  Verified,
  AccessTime,
  Star,
  School,
  WorkHistory,
  Language,
  Schedule,
  Favorite,
  FavoriteBorder,
  ArrowBack,
  Send,
  CheckCircle,
  Assignment,
} from "@mui/icons-material";
import {
  selectProfessionalById,
  toggleFavorite,
  fetchProfessionals,
} from "../redux/slices/professionalsSlice";
import Breadcrumbs from "../components/Breadcrumbs";
import "./ProfessionalProfile.css";

const ProfessionalProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactSent, setContactSent] = useState(false);

  const professional = useSelector((state) =>
    selectProfessionalById(state, id),
  );
  const favorites = useSelector((state) => state.professionals.favorites);
  const professionalsLoading = useSelector(
    (state) => state.professionals.loading,
  );

  const isFavorite = favorites.includes(parseInt(id));

  useEffect(() => {
    dispatch(fetchProfessionals());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFavoriteToggle = () => {
    dispatch(toggleFavorite(parseInt(id)));
  };

  const handleContactSubmit = () => {
    // Simuler l'envoi du message
    setContactSent(true);
    setTimeout(() => {
      setContactModalOpen(false);
      setContactSent(false);
      setContactMessage("");
    }, 2000);
  };

  const getDayName = (day) => {
    const days = {
      monday: "Lundi",
      tuesday: "Mardi",
      wednesday: "Mercredi",
      thursday: "Jeudi",
      friday: "Vendredi",
      saturday: "Samedi",
      sunday: "Dimanche",
    };
    return days[day] || day;
  };

  if (!professional) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {professionalsLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Alert severity="error">Professionnel non trouvé</Alert>
        )}
      </Container>
    );
  }

  const breadcrumbItems = [
    { label: "Accueil", path: "/" },
    { label: "Professionnels", path: "/professionals" },
    {
      label: `${professional.firstName} ${professional.lastName.charAt(0)}.`,
      path: `/professional/${id}`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          {professional.firstName} {professional.lastName.charAt(0)}. -{" "}
          {professional.discipline} - Mission Sport
        </title>
        <meta
          name="description"
          content={`Profil de ${professional.firstName} ${professional.lastName.charAt(0)}., ${professional.discipline} à ${professional.location}. ${professional.biography}`}
        />
        <meta
          property="og:title"
          content={`${professional.firstName} ${professional.lastName.charAt(0)}. - ${professional.discipline}`}
        />
        <meta property="og:description" content={professional.biography} />
        <meta property="og:image" content={professional.profileImage} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <main className="professional-profile-page">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Breadcrumbs */}
          <Box mb={3}>
            <Breadcrumbs items={breadcrumbItems} />
          </Box>

          {/* Bouton retour */}
          <Box mb={3}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/professionals")}
              variant="outlined"
            >
              Retour à la liste
            </Button>
          </Box>

          <Grid container spacing={4}>
            {/* Colonne principale */}
            <Grid item xs={12} md={8}>
              {/* En-tête du profil */}
              <Card className="profile-header" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item>
                      <Avatar
                        src={professional.profileImage}
                        alt={professional.fullName}
                        sx={{ width: 120, height: 120 }}
                        className="profile-avatar"
                      />
                    </Grid>
                    <Grid item xs>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography
                          variant="h4"
                          component="h1"
                          className="profile-name"
                        >
                          {professional.firstName}{" "}
                          {professional.lastName.charAt(0)}.
                        </Typography>
                        {professional.isVerified && (
                          <Verified color="success" />
                        )}
                        <IconButton
                          onClick={handleFavoriteToggle}
                          color={isFavorite ? "error" : "default"}
                        >
                          {isFavorite ? <Favorite /> : <FavoriteBorder />}
                        </IconButton>
                      </Box>

                      <Chip
                        label={professional.discipline}
                        color="primary"
                        size="medium"
                        sx={{ mb: 2 }}
                      />

                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationOn color="action" />
                        <Typography variant="body1">
                          {professional.location}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Rating
                          value={professional.rating}
                          precision={0.1}
                          readOnly
                        />
                        <Typography variant="body2" color="text.secondary">
                          {professional.rating}/5 ({professional.reviewsCount}{" "}
                          avis)
                        </Typography>
                      </Box>

                      <Typography
                        variant="h5"
                        color="primary"
                        className="profile-rate"
                      >
                        {professional.hourlyRate}
                        {professional.currency}/heure
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Biographie */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    À propos
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {professional.biography}
                  </Typography>
                </CardContent>
              </Card>

              {/* Spécialités */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Spécialités
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {professional.specialties.map((specialty, index) => (
                      <Chip
                        key={index}
                        label={specialty}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <School sx={{ mr: 1, verticalAlign: "middle" }} />
                    Certifications
                  </Typography>
                  <List>
                    {professional.certifications.map((cert, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText primary={cert} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              {/* Avis */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Star sx={{ mr: 1, verticalAlign: "middle" }} />
                    Avis clients
                  </Typography>
                  {professional.reviews.map((review) => (
                    <Box key={review.id} mb={2}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2">
                          {review.clientName}
                        </Typography>
                        <Rating value={review.rating} size="small" readOnly />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.date).toLocaleDateString("fr-FR")}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {review.comment}
                      </Typography>
                      {professional.reviews.indexOf(review) <
                        professional.reviews.length - 1 && (
                        <Divider sx={{ mt: 2 }} />
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* Actions */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Send />}
                      onClick={() => setContactModalOpen(true)}
                      fullWidth
                    >
                      Envoyer un message
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Assignment />}
                      fullWidth
                    >
                      Proposer une mission
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Informations */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informations
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <WorkHistory />
                      </ListItemIcon>
                      <ListItemText
                        primary="Expérience"
                        secondary={`${professional.experience} ans`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessTime />
                      </ListItemIcon>
                      <ListItemText
                        primary="Temps de réponse"
                        secondary={professional.responseTime}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText
                        primary="Missions réalisées"
                        secondary={professional.completedMissions}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Language />
                      </ListItemIcon>
                      <ListItemText
                        primary="Langues"
                        secondary={professional.languages.join(", ")}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              {/* Disponibilités */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Schedule sx={{ mr: 1, verticalAlign: "middle" }} />
                    Disponibilités
                  </Typography>
                  <List dense>
                    {Object.entries(professional.availability).map(
                      ([day, schedule]) => (
                        <ListItem key={day}>
                          <ListItemText
                            primary={getDayName(day)}
                            secondary={
                              schedule.available
                                ? schedule.slots.join(", ")
                                : "Non disponible"
                            }
                            secondaryTypographyProps={{
                              color: schedule.available
                                ? "text.secondary"
                                : "error",
                            }}
                          />
                        </ListItem>
                      ),
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Modal de contact */}
        <Dialog
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Contacter {professional.firstName} {professional.lastName.charAt(0)}
            .
          </DialogTitle>
          <DialogContent>
            {contactSent ? (
              <Box textAlign="center" py={3}>
                <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Message envoyé !
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {professional.firstName} {professional.lastName.charAt(0)}.
                  vous répondra dans les {professional.responseTime}.
                </Typography>
              </Box>
            ) : (
              <TextField
                autoFocus
                margin="dense"
                label="Votre message"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder={`Bonjour ${professional.firstName}, je suis intéressé(e) par vos services...`}
              />
            )}
          </DialogContent>
          {!contactSent && (
            <DialogActions>
              <Button onClick={() => setContactModalOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleContactSubmit}
                variant="contained"
                disabled={!contactMessage.trim()}
              >
                Envoyer
              </Button>
            </DialogActions>
          )}
        </Dialog>
      </main>
    </>
  );
};

export default ProfessionalProfile;
